import { VehicleOfferDto } from "@1games-ru/region-shared/dist/types/vehicle/vehicle-b";
import { useCallback, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider.ts";
import VehicleMpProvider from "@/core/data-provider/data-mp/vehicle/vehicle-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { clearOffer } from "@/domains/offer/slice";
import toast from "@/domains/toasts/helpers/toast";

export function useOfferCreatorVehicle(isShow: boolean) {
  const { t, i18n } = useTranslation("offer");
  const dispatch = useAppDispatch();
  const [vehicles, setVehicles] = useState<VehicleOfferDto[]>([]);
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(false);

  const close = useCallback(() => {
    dispatch(clearOffer());
    GameMpProvider.closeCurrentInterface(true);
  }, [dispatch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await VehicleMpProvider.getVehiclesOfferCreator();

      if (result instanceof RpcMpProviderFailedResponse) {
        console.error(result.error?.message ?? result);
        return;
      }

      setVehicles(result.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [setVehicles]);

  const create = useCallback(
    async (targetCharacterId: number, vehicleId: number, price: number) => {
      setPending(true);
      try {
        const result = await VehicleMpProvider.createTrade(vehicleId, targetCharacterId, price);

        if (result instanceof RpcMpProviderFailedResponse) {
          const code = result.error?.message;
          if (code && i18n.exists(`offer:offer.errors.${code}`)) {
            console.error(code);
            toast.error(t(`offer.errors.${code}`));
          } else {
            console.error(code ?? result);
            toast.error(t("offer.errors.createError"));
          }
          close();
          return;
        }

        toast.success(t("offer.toast.accepted"));
        close();
      } catch (e) {
        console.error(e);
        toast.error(t("offer.errors.createError"));
      } finally {
        setPending(false);
      }
    },
    [i18n, t, close],
  );

  useLayoutEffect(() => {
    if (isShow) {
      load();
    }
  }, [isShow, load]);

  return {
    vehicles,
    create,
    close,
    pending,
    loading,
  };
}
